import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { string, type InferType, object } from 'yup';
import { StyledInlineCancelAndSubmitButtons, StyledCancelEdit, StyledLinkButton } from '../styles';
import PrimaryButton from '@/components/checkout/uielements/primaryButton';
import { StyledFormField } from '@/components/uielements/form-fields/styles';
import { Form, Input } from '@/components/uielements/formik';
import { VALIDATION_STRINGS } from '@/constants/validation';
import meActions from '@/redux/me/actions';
import { type RootState } from '@/types/app';

interface Props {
  onCancel: () => void;
}

// Validation schema for the reset form.
const birthdaySchema = object({
  birthday: string().required(VALIDATION_STRINGS.REQUIRED),
});

type FormValues = InferType<typeof birthdaySchema>;

export const UserBirthdayForm = (props: Props) => {
  const dispatch = useDispatch();
  const { loading, data } = useSelector((state: RootState) => state.app.me);
  const { readonly_fields } = useSelector(
    (state: RootState) => state.app.organization.external_auth,
  );
  const userFields = readonly_fields.user;

  const handleBirthdaySubmit = (formValues: FormValues) => {
    dispatch(
      meActions.updateMe({
        birthday: formValues.birthday,
      }),
    );
  };

  return (
    <Formik
      validateOnBlur
      validateOnChange={false}
      onSubmit={(formValues, { resetForm }) => {
        handleBirthdaySubmit(formValues);
        resetForm();
        props.onCancel();
      }}
      /** @TODO ensure that `data` is defined. */
      // @ts-expect-error
      initialValues={{ birthday: data.birthday ?? '' }}
      validationSchema={birthdaySchema}
    >
      {({ dirty, isValid }) => (
        <Form>
          <StyledFormField>
            <Input
              data-testid={'birthday-input'}
              autoFocus
              id="birthday"
              name="birthday"
              type="date"
              label={''}
              width={'50%'}
              disabled={userFields.includes('birthday')}
            />
          </StyledFormField>
          <StyledInlineCancelAndSubmitButtons>
            {/* Cancel button */}
            <StyledCancelEdit>
              <Link onClick={props.onCancel} aria-label="Hide birthday form">
                Cancel
              </Link>
            </StyledCancelEdit>

            {/* Submit Button */}
            <PrimaryButton
              type="submit"
              theme="secondary"
              disabled={!dirty || !isValid}
              $submitting={loading}
              handleClick={() => null}
              $minWidth={150}
              aria-label="Submit Birthday"
            >
              {loading ? 'Loading' : 'Submit'}
            </PrimaryButton>
          </StyledInlineCancelAndSubmitButtons>
        </Form>
      )}
    </Formik>
  );
};

const Link = styled(StyledLinkButton)`
  margin: '0px';
`;
